import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { AlertCircle, Link2 } from 'lucide-react';
import {
  useCancelExecution,
  useDeletePlan,
  useLockPlan,
  useNetworkPlan,
  useRetryPlan,
  useStartExecution,
  useUnlockPlan,
  useUpdatePlan,
} from '../../hooks/useNetworkPlans';
import { useAttachPlan, useCreateCM } from '@/features/changeManagement';
import { useFeature } from '@/hooks/useCapabilities.ts';
import PlanStepEditor from '../../components/PlanStepEditor';
import ConfirmationModal from '@/components/ConfirmationModal';
import { useConfirm } from '@/hooks/useConfirm';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AppSheet } from '@/components/ui/app-sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import { PageContainer } from '@/components/ui/page-container';
import { cn } from '@/lib/utils';

const STATE_STYLE: Record<string, string> = {
  draft: 'bg-muted text-muted-foreground',
  locked: 'bg-sev-3/15 text-foreground',
  executing: 'bg-sev-5/15 text-sev-5',
  completed: 'bg-sev-4/15 text-sev-4',
  failed: 'bg-sev-1/15 text-sev-1',
  cancelled: 'bg-sev-3/30 text-foreground',
};

export default function NetworkPlanDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: plan, isLoading, isError, error } = useNetworkPlan(id);

  const updatePlanMutation = useUpdatePlan();
  const deletePlanMutation = useDeletePlan();

  const lockPlanMutation = useLockPlan();
  const unlockPlanMutation = useUnlockPlan();
  const startExecutionMutation = useStartExecution();
  const cancelExecutionMutation = useCancelExecution();
  const retryPlanMutation = useRetryPlan();
  const createCMMutation = useCreateCM();
  const attachPlanMutation = useAttachPlan();
  const cmEnabled = useFeature('changeManagement');

  const [isEditModalActive, setIsEditModalActive] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const isDraft = plan?.state === 'draft';
  const isLocked = plan?.state === 'locked';
  const isExecuting = plan?.state === 'executing';
  const isTerminal =
    plan?.state === 'completed' || plan?.state === 'failed' || plan?.state === 'cancelled';

  const openEditModal = () => {
    if (!plan) return;
    setEditTitle(plan.title);
    setEditDescription(plan.description || '');
    setIsEditModalActive(true);
  };

  const handleEditSubmit = async (e: React.SubmitEvent) => {
    e.preventDefault();
    if (!id) return;
    try {
      await updatePlanMutation.mutateAsync({
        planId: id,
        data: { title: editTitle, description: editDescription || undefined },
      });
      setIsEditModalActive(false);
    } catch {
      // ignore
    }
  };

  const confirmDelete = async () => {
    if (!id) return;
    try {
      await deletePlanMutation.mutateAsync(id);
      void navigate('/network-plans');
    } catch {
      // ignore
    } finally {
      setIsDeleteOpen(false);
    }
  };

  const handleLock = () => {
    if (!id) return;
    lockPlanMutation.mutate(id);
  };

  const { confirm, confirmDialog } = useConfirm();

  const handleUnlock = () => {
    if (!id) return;
    unlockPlanMutation.mutate(id);
  };

  const handleStartExecution = async () => {
    if (!id) return;
    const ok = await confirm({
      title: 'Start execution',
      message: 'Start execution? Automated steps will begin running immediately.',
      confirmText: 'Start',
    });
    if (!ok) return;
    startExecutionMutation.mutate(id);
  };

  const handleCancel = async () => {
    if (!id) return;
    const ok = await confirm({
      title: 'Cancel execution',
      message: 'Cancel execution? In-progress steps will be marked as failed.',
      confirmText: 'Cancel execution',
      cancelText: 'Keep running',
      isDanger: true,
    });
    if (!ok) return;
    cancelExecutionMutation.mutate(id);
  };

  const handleRetryPlan = async () => {
    if (!id) return;
    const ok = await confirm({
      title: 'Retry failed steps',
      message: 'Retry all failed steps? Previously failed attempts are preserved in the audit log.',
      confirmText: 'Retry',
    });
    if (!ok) return;
    retryPlanMutation.mutate(id);
  };

  // Wrap this draft plan in a new change request (draft CM) and jump to it, so the
  // plan can go through approval/scheduling without rebuilding it from the CM side.
  const handleCreateChangeRequest = async () => {
    if (!plan) return;
    try {
      const newCm = await createCMMutation.mutateAsync({
        contextId: plan.contextId,
        title: plan.title,
        description: plan.description || undefined,
      });
      await attachPlanMutation.mutateAsync({ cmId: newCm.id, data: { planId: plan.id } });
      void navigate(`/change-management/${newCm.id}`);
    } catch {
      // ignore
    }
  };

  const isAnyExecutionPending =
    lockPlanMutation.isPending ||
    unlockPlanMutation.isPending ||
    startExecutionMutation.isPending ||
    cancelExecutionMutation.isPending ||
    retryPlanMutation.isPending;

  if (isLoading) {
    return (
      <PageContainer>
        <Progress value={undefined} className="h-1" />
      </PageContainer>
    );
  }

  if (isError || !plan) {
    return (
      <PageContainer className="space-y-4">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error?.message || 'Plan not found'}</AlertDescription>
        </Alert>
        <Button asChild variant="outline">
          <Link to="/network-plans">Back to plans</Link>
        </Button>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <nav className="text-sm text-muted-foreground mb-4" aria-label="breadcrumbs">
        <Link to="/network-plans" className="hover:underline">
          Plans
        </Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">{plan.title}</span>
      </nav>

      {plan.cmId ? (
        <Alert className="mb-4 border-sev-5/30 bg-sev-5/5">
          <Link2 className="h-4 w-4" />
          <AlertDescription>
            Linked to CM:{' '}
            <Link
              to={`/change-management/${plan.cmId}`}
              className="font-semibold hover:underline"
            >
              {plan.cmTitle}
            </Link>
            {plan.state === 'locked' || plan.state === 'executing' ? (
              <span className="ml-2 text-muted-foreground">
                — Plan state is managed by the CM lifecycle
              </span>
            ) : null}
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 md:gap-4 mb-6">
        <div className="min-w-0">
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight truncate">
            {plan.title}
          </h1>
          {plan.description ? (
            <p className="text-sm text-muted-foreground mt-1">{plan.description}</p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <Badge className={cn('border-0 font-normal capitalize', STATE_STYLE[plan.state])}>
            {plan.state}
          </Badge>
          {isDraft ? (
            <>
              <Button variant="outline" size="sm" onClick={openEditModal}>
                Edit
              </Button>
              {plan.stepCount > 0 ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLock}
                  disabled={isAnyExecutionPending || lockPlanMutation.isPending}
                >
                  {lockPlanMutation.isPending ? 'Locking…' : 'Lock plan'}
                </Button>
              ) : null}
              {!plan.cmId && cmEnabled ? (
                <Button
                  size="sm"
                  onClick={() => void handleCreateChangeRequest()}
                  disabled={createCMMutation.isPending || attachPlanMutation.isPending}
                >
                  {createCMMutation.isPending ? 'Creating…' : 'Create change request'}
                </Button>
              ) : null}
              <Button
                variant="outline"
                size="sm"
                className="text-sev-1 hover:text-sev-1"
                onClick={() => { setIsDeleteOpen(true); }}
              >
                Delete
              </Button>
            </>
          ) : null}
          {isLocked ? (
            <>
              <Button
                size="sm"
                onClick={() => void handleStartExecution()}
                disabled={isAnyExecutionPending || startExecutionMutation.isPending}
              >
                {startExecutionMutation.isPending ? 'Starting…' : 'Start execution'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleUnlock}
                disabled={isAnyExecutionPending || unlockPlanMutation.isPending}
              >
                {unlockPlanMutation.isPending ? 'Unlocking…' : 'Unlock'}
              </Button>
            </>
          ) : null}
          {isExecuting ? (
            <Button
              variant="outline"
              size="sm"
              className="text-sev-1 hover:text-sev-1"
              onClick={() => void handleCancel()}
              disabled={isAnyExecutionPending || cancelExecutionMutation.isPending}
            >
              {cancelExecutionMutation.isPending ? 'Cancelling…' : 'Cancel execution'}
            </Button>
          ) : null}
          {plan.state === 'failed' ? (
            <Button
              size="sm"
              className="bg-sev-3 text-foreground hover:bg-sev-3/80"
              onClick={() => void handleRetryPlan()}
              disabled={isAnyExecutionPending || retryPlanMutation.isPending}
            >
              {retryPlanMutation.isPending ? 'Retrying…' : 'Retry failed steps'}
            </Button>
          ) : null}
          {isTerminal && plan.state === 'completed' ? (
            <Badge className="bg-sev-4/15 text-sev-4 border-0 font-normal">Completed</Badge>
          ) : null}
        </div>
      </div>

      {isExecuting ? (
        <Alert className="mb-4 border-sev-5/30 bg-sev-5/5">
          <AlertDescription>
            <Progress
              value={
                plan.stepCount > 0 ? (plan.completedStepCount / plan.stepCount) * 100 : 0
              }
              className="h-1.5 mb-1.5"
            />
            <p className="text-xs text-muted-foreground">
              {plan.completedStepCount} of {plan.stepCount} steps completed
            </p>
          </AlertDescription>
        </Alert>
      ) : null}

      <section className="rounded-lg border border-border bg-card p-4">
        <PlanStepEditor planId={plan.id} />
      </section>

      <AppSheet
        open={isEditModalActive}
        onOpenChange={(open) => { if (!open) setIsEditModalActive(false); }}
        title="Edit plan"
        size="md"
      >
        <AppSheet.Body>
          <form
            id="edit-plan-form"
            onSubmit={(e) => void handleEditSubmit(e)}
            className="space-y-4"
          >
            <div className="space-y-1.5">
              <Label htmlFor="ep-title">Title *</Label>
              <Input
                id="ep-title"
                type="text"
                value={editTitle}
                onChange={(e) => { setEditTitle(e.target.value); }}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ep-desc">Description</Label>
              <Textarea
                id="ep-desc"
                value={editDescription}
                onChange={(e) => { setEditDescription(e.target.value); }}
                rows={3}
              />
            </div>
          </form>
        </AppSheet.Body>
        <AppSheet.Footer>
          <Button
            variant="outline"
            onClick={() => { setIsEditModalActive(false); }}
            disabled={updatePlanMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="edit-plan-form"
            disabled={updatePlanMutation.isPending}
          >
            {updatePlanMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </AppSheet.Footer>
      </AppSheet>

      <ConfirmationModal
        isActive={isDeleteOpen}
        title="Delete plan"
        message={`Delete "${plan.title}"? This permanently removes the plan and all its steps and can't be undone.`}
        confirmText="Delete"
        isDanger
        isLoading={deletePlanMutation.isPending}
        onConfirm={() => { void confirmDelete(); }}
        onCancel={() => { setIsDeleteOpen(false); }}
      />
      {confirmDialog}
    </PageContainer>
  );
}
